/**
 * Overview: the dashboard's home, and where usage is read.
 *
 * Two kinds of number live here, kept apart because they answer different questions. "This billing
 * period" is what the plan limit applies to -- whether the next request succeeds -- and does not move
 * with the time range. "Activity" is what traffic looked like over the range chosen beside it. A
 * 7-day view of credits would understate consumption against a monthly allowance, so credits are
 * only ever shown for the period.
 *
 * This page took in the former Usage page, which repeated half of it.
 */
import { useMemo, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { cn } from '@urlshot/ui';
import {
  useApiKeys,
  useSession,
  useUsageSummary,
  useWorkspace,
  type UsageSummary,
  type WorkspaceDetail,
} from '../../lib/api';
import { isBillingConfigured } from '../../env';
import { CreditMeter, NEARLY_USED } from '../../components/credit-meter';
import { RequestsChart } from '../../components/requests-chart';
import { Button, Card, SegmentedControl } from '../../components/ui';

const RANGES = [
  { value: '1', label: '24 hours' },
  { value: '7', label: '7 days' },
  { value: '30', label: '30 days' },
  { value: '90', label: '90 days' },
] as const;

type RangeValue = (typeof RANGES)[number]['value'];

/** A week: long enough to show a pattern, short enough that six-hour bars still show each day. */
const DEFAULT_RANGE: RangeValue = '7';

/**
 * How requests in the range ended, one column each in a single grey row.
 *
 * Columns rather than one line of text, so a rise in failures is noticed rather than read past; one
 * shaded row rather than four bordered cards, so the outcomes read as a breakdown of the Requests
 * figure above them instead of four more things to look at. The colours follow the request log's
 * status badges; the hover text says what each outcome costs, which is the question a customer
 * looking at a failure count usually has.
 */
const OUTCOMES = [
  {
    key: 'succeeded',
    label: 'Succeeded',
    dot: 'bg-green-600',
    meaning: 'Captured and returned. Costs one credit, or nothing when served from the cache.',
  },
  {
    key: 'failed',
    label: 'Failed',
    dot: 'bg-red-600',
    meaning: 'The page could not be captured: it failed to load, timed out, or the render failed.',
  },
  {
    key: 'rejected',
    label: 'Rejected',
    dot: 'bg-neutral-400',
    meaning:
      "Refused before rendering: an invalid parameter, key or signature, a blocked address, or this month's credits used up. Costs nothing.",
  },
  {
    key: 'throttled',
    label: 'Throttled',
    dot: 'bg-amber-500',
    meaning:
      'Over the per-second or concurrent-render limit. Safe to retry after the Retry-After delay. Costs nothing.',
  },
] as const;

function windowFor(days: number): { from: string; to: string } {
  const to = new Date();
  const from = new Date(to.getTime() - days * 24 * 60 * 60 * 1000);
  return { from: from.toISOString(), to: to.toISOString() };
}

/** A dash rather than "0%" for an empty range, which would read as a problem rather than no data. */
function percent(part: number, whole: number): string {
  return whole === 0 ? '—' : `${String(Math.round((part / whole) * 100))}%`;
}

function formatCount(value: number): string {
  return value.toLocaleString('en-US');
}

/**
 * A grey row of four columns with dividers between them.
 *
 * Shared by the Activity figures and the outcomes beneath them, so their columns line up exactly: the
 * outcomes read as the breakdown of the figures above rather than as a separate set of things.
 */
const STAT_ROW =
  'grid grid-cols-2 rounded-md bg-neutral-50 sm:grid-cols-4 sm:divide-x sm:divide-neutral-200';

/** Padding for a column inside a {@link STAT_ROW}. */
const STAT_CELL = 'px-4 py-3';

function Figure({
  label,
  value,
  testId,
  className,
}: {
  label: string;
  value: string;
  testId?: string;
  className?: string;
}) {
  return (
    <div className={className}>
      <p className="text-xs uppercase tracking-wide text-neutral-500">{label}</p>
      <p className="mt-1 text-lg font-semibold" data-testid={testId}>
        {value}
      </p>
    </div>
  );
}

export function OverviewRoute() {
  const { data: session } = useSession();
  const { data: workspace, isLoading } = useWorkspace();
  const { data: keys } = useApiKeys();
  const [range, setRange] = useState<RangeValue>(DEFAULT_RANGE);

  // Memoized on the range, and it has to be. `windowFor` ends at `new Date()`, so calling it inline
  // would produce a different window on every render, change the query key every time, and leave
  // the page refetching forever without ever leaving its loading state.
  const usageWindow = useMemo(() => windowFor(Number(range)), [range]);
  const { data: usage, isError } = useUsageSummary(usageWindow);

  const activeKeys = (keys?.keys ?? []).filter((key) => key.status === 'active');
  const needsFirstKey = keys !== undefined && activeKeys.length === 0;

  // Only the owner can buy a plan, so only the owner is asked to. Without billing configured the
  // Billing page has nothing to sell, and a banner pointing there would lead nowhere.
  const offerUpgrade =
    isBillingConfigured &&
    workspace !== undefined &&
    workspace.planCode === 'free' &&
    workspace.membership.role === 'owner';

  return (
    <>
      <div>
        <h1 className="text-xl font-semibold">
          Welcome
          {session?.user.displayName !== null && session?.user.displayName !== undefined
            ? `, ${session.user.displayName}`
            : ''}
        </h1>
        <p className="mt-1 text-sm text-neutral-600">{workspace?.name ?? 'Your workspace'}</p>
      </div>

      {session?.user.emailVerified === false && (
        <Card className="border-amber-200 bg-amber-50">
          <h2 className="text-base font-semibold text-amber-900">Verify your email</h2>
          <p className="mt-1 text-sm text-amber-900">
            Open the verification link we sent you to confirm your address.
          </p>
        </Card>
      )}

      {needsFirstKey && (
        <Card className="border-brand-400 ring-1 ring-brand-400">
          <h2 className="text-base font-semibold">Create your first API key</h2>
          <p className="mt-1 text-sm text-neutral-600">
            You need a key to make a screenshot request. It is shown once, so keep it somewhere
            safe.
          </p>
          <Link to="/api-keys" className="mt-4 inline-block">
            <Button data-testid="onboarding-create-key">Create an API key</Button>
          </Link>
        </Card>
      )}

      {offerUpgrade && (
        <UpgradeBanner workspace={workspace} creditsUsed={usage?.creditsUsedThisPeriod} />
      )}

      {/*
        The workspace at a glance. Kept out of "This billing period": the plan and the keys are not
        figures of a period, and beside the credit bar they read as if they were.
      */}
      <div className="grid gap-4 sm:grid-cols-3">
        <Card>
          <Figure
            label="Plan"
            value={isLoading ? '—' : (workspace?.planName ?? 'Free')}
            testId="overview-plan"
          />
        </Card>
        <Card>
          <Figure
            label="Monthly credits"
            value={workspace === undefined ? '—' : formatCount(workspace.limits.monthlyCredits)}
            testId="overview-monthly-credits"
          />
        </Card>
        <Card>
          <Figure
            label="Active API keys"
            value={keys === undefined ? '—' : String(activeKeys.length)}
            testId="overview-active-keys"
          />
        </Card>
      </div>

      <Card data-testid="overview-period">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="text-base font-semibold">This billing period</h2>
          <Link to="/billing" className="link text-sm">
            Plans and billing
          </Link>
        </div>

        {usage !== undefined && (
          <div className="mt-3">
            <CreditMeter
              used={usage.creditsUsedThisPeriod}
              included={usage.monthlyCredits}
              resetsAt={usage.billingPeriod.endsAt}
              data-testid="overview-credit-meter"
            />
          </div>
        )}
      </Card>

      <Card data-testid="overview-activity">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-base font-semibold">Activity</h2>
            <p className="mt-1 text-sm text-neutral-600">
              Requests over the range you choose. The credits above count the whole billing period.{' '}
              <Link to="/request-logs" className="link">
                View request logs
              </Link>
            </p>
          </div>
          <SegmentedControl label="Time range" options={RANGES} value={range} onChange={setRange} />
        </div>

        {isError && <p className="mt-4 text-sm text-red-700">Usage could not be loaded.</p>}
        {usage !== undefined && <Activity usage={usage} />}
      </Card>
    </>
  );
}

/**
 * The chart and the figures for the chosen range.
 *
 * Always all of them, a quiet range included: counts read zero, and rates and averages -- which have
 * no value without requests -- read as a dash. The section keeps its shape, so switching to a range
 * with no traffic does not make half the page disappear.
 */
function Activity({ usage }: { usage: UsageSummary }) {
  const { totals } = usage;
  const noRequests = totals.requests === 0;

  return (
    <>
      <RequestsChart hourly={usage.hourly} from={usage.window.from} to={usage.window.to} />

      <div className={cn('mt-6', STAT_ROW)} data-testid="overview-stats">
        <Figure
          label="Requests"
          value={formatCount(totals.requests)}
          testId="overview-requests"
          className={STAT_CELL}
        />
        <Figure
          label="Success rate"
          value={percent(totals.succeeded, totals.requests)}
          className={STAT_CELL}
        />
        <Figure
          label="Cache hit rate"
          value={percent(totals.cacheHits, totals.requests)}
          className={STAT_CELL}
        />
        <Figure
          label="Average duration"
          value={noRequests ? '—' : `${formatCount(totals.averageDurationMs)} ms`}
          className={STAT_CELL}
        />
      </div>

      <div className={cn('mt-3', STAT_ROW)} data-testid="overview-outcomes">
        {OUTCOMES.map((outcome) => (
          <div
            key={outcome.key}
            className={STAT_CELL}
            title={outcome.meaning}
            data-testid={`overview-outcome-${outcome.key}`}
          >
            <p className="flex items-center gap-2 text-xs uppercase tracking-wide text-neutral-500">
              <span className={cn('size-2 rounded-full', outcome.dot)} aria-hidden="true" />
              {outcome.label}
            </p>
            <p className="mt-1 text-lg font-semibold">{formatCount(totals[outcome.key])}</p>
            <p className="text-xs text-neutral-500">
              {noRequests ? '—' : `${percent(totals[outcome.key], totals.requests)} of requests`}
            </p>
          </div>
        ))}
      </div>
    </>
  );
}

type UpgradeTone = 'plain' | 'nearly' | 'exhausted';

/**
 * The upgrade prompt for a Free workspace.
 *
 * One sentence and a button. The numbers are in "This billing period" just below, so the banner does
 * not repeat them; it only changes what it says as the month's credits run down, which is when an
 * upgrade stops being a suggestion and becomes the way to keep working. Not dismissible for the
 * same reason: it is quiet until it matters.
 */
function UpgradeBanner({
  workspace,
  creditsUsed,
}: {
  workspace: WorkspaceDetail;
  /** Undefined while usage loads; the banner then says the plain thing rather than guessing. */
  creditsUsed: number | undefined;
}) {
  const limit = workspace.limits.monthlyCredits;
  const share = creditsUsed === undefined || limit <= 0 ? 0 : creditsUsed / limit;
  const tone: UpgradeTone = share >= 1 ? 'exhausted' : share >= NEARLY_USED ? 'nearly' : 'plain';

  // The period's end is exclusive, the first instant of the next one, so it is the reset date.
  const resetsOn = new Date(workspace.billingPeriod.endsAt).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
  });

  const message: Record<UpgradeTone, string> = {
    plain: "You're on the Free plan. Upgrade for more screenshots and more renders at a time.",
    nearly: "You're close to this month's free limit. Upgrade to keep going.",
    exhausted: `This month's free screenshots are used up. They reset on ${resetsOn}, or upgrade now.`,
  };

  const style: Record<UpgradeTone, string> = {
    plain: 'border-neutral-200 bg-neutral-50 text-neutral-800',
    nearly: 'border-amber-200 bg-amber-50 text-amber-900',
    exhausted: 'border-red-200 bg-red-50 text-red-900',
  };

  return (
    <Card
      data-testid="upgrade-banner"
      data-tone={tone}
      className={`flex flex-wrap items-center justify-between gap-3 py-3 ${style[tone]}`}
    >
      <p className="text-sm">{message[tone]}</p>
      <Link to="/billing">
        <Button data-testid="upgrade-banner-cta">Upgrade</Button>
      </Link>
    </Card>
  );
}
